<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button
                variant="link"
                class="list-view-icon-btn"
                :class="{ 'list-view-icon-btn--active': filtersOpen }"
                v-b-tooltip.hover.bottom
                :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                @click="filtersOpen = !filtersOpen"
              >
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 180px; max-width: 220px;">
              <label class="filter-label">Lista de precios</label>
              <v-select :options="priceListOptions" v-model="filters.priceList" placeholder="Todas las activas" :select-on-tab="true" :disabled="snapshot.active" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Buscar producto</label>
              <b-form-input size="sm" v-model="filters.search" placeholder="Nombre, código o código de barras" :disabled="snapshot.active" @keyup.enter="search" />
            </div>
            <div class="list-view-filter" style="width: 110px;">
              <label class="filter-label">Precio mínimo</label>
              <b-form-input size="sm" type="number" min="0" v-model.number="filters.priceMin" :disabled="snapshot.active" @keyup.enter="search" />
            </div>
            <div class="list-view-filter" style="width: 110px;">
              <label class="filter-label">Precio máximo</label>
              <b-form-input size="sm" type="number" min="0" v-model.number="filters.priceMax" :disabled="snapshot.active" @keyup.enter="search" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 320px;">
              <label class="filter-label">Regla de revisión</label>
              <v-select
                :options="reviewRuleOptions"
                v-model="filters.dirtyRule"
                :select-on-tab="true"
                :clearable="false"
                :disabled="snapshot.active"
              />
            </div>
            <b-button variant="outline-dark" size="sm" :disabled="snapshot.active" @click="search" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
          <div v-if="filters.dirtyRule" class="price-audit-review-hint">
            <AppIcon name="info-circle" />
            <span>{{ reviewRuleHint }}</span>
          </div>
        </b-collapse>

        <div class="price-audit-snapshot-status" :class="{ 'price-audit-snapshot-status--active': snapshot.active }">
          <div class="price-audit-snapshot-status__identity">
            <span class="price-audit-snapshot-status__icon">
              <b-spinner v-if="snapshot.active" small />
              <AppIcon v-else name="schedule" />
            </span>
            <div>
              <strong v-if="snapshot.active">Actualizando precios</strong>
              <strong v-else>Snapshot de precios</strong>
              <div v-if="snapshot.active" class="price-audit-snapshot-status__detail">
                Estamos recalculando el catálogo. La auditoría se habilitará automáticamente al finalizar.
              </div>
              <div v-else class="price-audit-snapshot-status__detail">
                <template v-if="oldestSnapshotAgeLabel">Registro más antiguo: {{ oldestSnapshotAgeLabel }} (según los resultados mostrados).</template>
                <template v-else>Actualizá el catálogo antes de iniciar la auditoría.</template>
              </div>
            </div>
          </div>
          <b-button
            v-if="!snapshot.active"
            variant="link"
            size="sm"
            class="list-view-ghost-btn price-audit-snapshot-status__action"
            :disabled="snapshot.loading"
            @click="openSnapshotConfirmation"
          >
            <b-spinner v-if="snapshot.loading" small class="mr-1" />
            <AppIcon v-else name="refresh" class="mr-1" />
            Actualizar precios
          </b-button>
        </div>
      </b-card>

      <b-overlay :show="snapshot.active" opacity="0.97" rounded="sm" class="price-audit-workspace">
        <template v-slot:overlay>
          <div class="price-audit-process-overlay">
            <b-spinner variant="primary" label="Actualizando precios" />
            <strong>Actualizando precios</strong>
            <span>Esperá a que termine el cálculo para auditar y aplicar ajustes.</span>
          </div>
        </template>
      <b-row>
        <b-col lg="12">
          <b-card>
            <b-row v-if="rows.length">
              <b-col sm="12" class="mb-2 d-flex align-items-center flex-wrap price-audit-select-bar">
                <b-dropdown v-if="!selected.length" text="Seleccionar" size="sm" variant="outline-dark">
                  <b-dropdown-item @click="selectPage()">
                    Esta página ({{ rows.length }})
                  </b-dropdown-item>
                  <b-dropdown-item :disabled="selectingAll" @click="selectAllMatching()">
                    <b-spinner small v-if="selectingAll" class="mr-1" />
                    Todos los resultados del filtro ({{ pagination.total }})
                  </b-dropdown-item>
                </b-dropdown>
                <b-button v-else variant="outline-secondary" size="sm" @click="clearSelection()">
                  <AppIcon name="close" class="mr-1" />Borrar selección ({{ selected.length }})
                </b-button>
                <div v-if="selected.length" class="price-audit-bulk-actions">
                  <span class="price-audit-bulk-actions__count">redondear a:</span>
                  <b-button variant="dark" size="sm" @click="openBulkModal(1, 'Sin decimales')">Sin decimales</b-button>
                  <b-button variant="dark" size="sm" @click="openBulkModal(5, 'Múltiplo de 5')">Múltiplo de 5</b-button>
                  <b-button variant="dark" size="sm" @click="openBulkModal(10, 'Múltiplo de 10')">Múltiplo de 10</b-button>
                  <b-button variant="dark" size="sm" @click="openBulkModal(100, 'Múltiplo de 100')">Múltiplo de 100</b-button>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="12">
                <b-table
                  class="app-table"
                  :items="rows"
                  :fields="fields"
                  sticky-header="60vh"
                  small
                  :stacked="isMobileViewport ? 'md' : false"
                  v-if="rows.length"
                >
                  <template v-slot:cell(select_multi)="data">
                    <button
                      type="button"
                      class="price-audit-row-select"
                      :class="{ 'price-audit-row-select--selected': isSelected(data.item) }"
                      :aria-label="isSelected(data.item) ? 'Quitar producto de la selección' : 'Seleccionar producto'"
                      :aria-pressed="isSelected(data.item) ? 'true' : 'false'"
                      @click.stop="toggleSelect(data.item)"
                    >
                      <AppIcon :name="isSelected(data.item) ? 'check2-square' : 'square'"
                               class="price-audit-row-select__icon"
                               font-scale="1.3" />
                      <span class="price-audit-row-select__label">{{ isSelected(data.item) ? 'Seleccionado' : 'Seleccionar' }}</span>
                    </button>
                  </template>
                  <template v-slot:cell(product_name)="data">
                    <div class="price-audit-product-name">{{ data.item.product_name }}</div>
                    <small class="price-audit-product-code">{{ data.item.product_code }}</small>
                  </template>
                  <template v-slot:cell(price_list_name)="data">
                    <b-badge pill variant="light" class="price-audit-list-badge">
                      {{ data.item.price_list_name }}
                    </b-badge>
                  </template>
                  <template v-slot:cell(price)="data">
                    <div class="price-audit-price-cell">
                      <strong class="price-audit-price-cell__value">{{ fPrice(data.item.price) }}</strong>
                      <b-badge
                      v-if="data.item.snapshot_updated_at"
                      pill
                      :variant="isSnapshotNewerThanBase(data.item) ? 'success' : 'secondary'"
                      class="price-audit-updated-badge"
                      :class="{ 'price-audit-updated-badge--newer': isSnapshotNewerThanBase(data.item) }"
                      v-b-tooltip.hover
                      :title="snapshotBadgeTitle(data.item)"
                      >
                        <AppIcon name="schedule" /> {{ snapshotAge(data.item.snapshot_updated_at) }}
                      </b-badge>
                      <b-badge v-else pill variant="warning" class="price-audit-updated-badge">
                        <AppIcon name="schedule" /> Sin registro
                      </b-badge>
                    </div>
                  </template>
                  <template v-slot:cell(utility)="data">
                    <div class="price-audit-utility-cell">
                      <b-badge pill variant="primary" class="price-audit-utility-badge">
                        {{ fUtility(effectiveUtilityOf(data.item)) }}%
                      </b-badge>
                      <small v-if="isUsingListDefault(data.item)" class="price-audit-utility-cell__source">Default lista</small>
                    </div>
                  </template>
                  <template v-slot:cell(status)="data">
                    <div class="price-audit-status-content">
                      <b-badge
                        v-if="data.item.snapshot_stale"
                        variant="warning"
                        v-b-tooltip.hover
                        title="El costo o la utilidad de este producto cambiaron después del último cálculo — puede no ser exacto"
                      >
                        <AppIcon name="exclamation-triangle" /> Desactualizado
                      </b-badge>
                      <b-badge v-else-if="data.item.just_updated" variant="success">
                        <AppIcon name="check2" /> Actualizado
                      </b-badge>
                    </div>
                  </template>
                </b-table>

                <EmptyState
                  v-else-if="!loading"
                  icon="price_check"
                  title="No se encontraron precios con esos filtros"
                  description="Ajustá los filtros de arriba y volvé a buscar." />

                <div v-if="rows.length" class="app-table-footer">
                  <span class="list-view-toolbar__count">
                    {{ pagination.total }} resultado{{ pagination.total == 1 ? '' : 's' }}
                  </span>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
      </b-overlay>

      <b-row>
        <b-col lg="12">
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="pagination.total">
                <b-pagination
                  class="mb-0"
                  size="sm"
                  pills
                  :total-rows="pagination.total"
                  :per-page="pagination.per_page"
                  :disabled="snapshot.active"
                  v-model="pagination.current_page"
                  @input="search" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-col>

    <b-modal
      v-model="snapshot.confirm"
      size="md"
      hide-footer
      :no-close-on-backdrop="snapshot.loading"
      :no-close-on-esc="snapshot.loading"
      :hide-header-close="snapshot.loading"
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="refresh" /></span>
        <h5 class="app-modal-header__title">Actualizar snapshot de precios</h5>
      </div>
      <p>
        Este proceso actualiza la foto de los precios finales calculados, con costo, utilidad e IVA, que utiliza esta auditoría.
      </p>
      <p class="text-muted small">
        Corre en segundo plano y puede tardar algunos minutos. Durante la actualización no se podrán aplicar ajustes en la auditoría.
      </p>
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-2" :disabled="snapshot.loading" @click="snapshot.confirm = false">Cancelar</b-button>
        <b-button variant="dark" :disabled="snapshot.loading" @click="refreshSnapshot">
          <b-spinner small v-if="snapshot.loading" class="mr-1" />
          <AppIcon v-else name="refresh" class="mr-1" />
          {{ snapshot.loading ? 'Iniciando...' : 'Confirmar' }}
        </b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="bulkConfirm.show"
      size="md"
      hide-footer
      :no-close-on-backdrop="applying"
      :no-close-on-esc="applying"
      :hide-header-close="applying"
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="save" /></span>
        <h5 class="app-modal-header__title">Guardar ajustes de precios</h5>
      </div>
      <p>
        ¿Confirmás guardar el ajuste de {{ bulkModalValidCount }} producto{{ bulkModalValidCount == 1 ? '' : 's' }}?
      </p>
      <p class="text-muted small">
        El sistema recalculará la utilidad exacta de cada producto para conservar el precio final seleccionado.
      </p>
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-2" :disabled="applying" @click="bulkConfirm.show = false">Cancelar</b-button>
        <b-button variant="dark" :disabled="applying" @click="confirmBulk">
          <b-spinner small v-if="applying" class="mr-1" />
          <AppIcon v-else name="save" class="mr-1" />
          {{ applying ? 'Guardando...' : 'Confirmar y guardar' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Previsualización del ajuste en lote antes de guardar -->
    <b-modal
      v-model="bulkModal.show"
      :title="'Aplicar: ' + bulkModal.label"
      size="lg"
      ok-title="Confirmar y guardar"
      :ok-disabled="applying || !bulkModalValidCount"
      :cancel-disabled="applying"
      :hide-header-close="applying"
      :no-close-on-backdrop="applying"
      :no-close-on-esc="applying"
      cancel-title="Cancelar"
      @ok.prevent="openBulkConfirmation"
    >
      <template v-slot:modal-ok>
        <b-spinner v-if="applying" small class="mr-1" />
        {{ applying ? 'Guardando ajustes…' : 'Confirmar y guardar' }}
      </template>

      <b-overlay :show="applying" opacity="0.96" rounded="sm">
        <template v-slot:overlay>
          <div class="price-audit-apply-progress">
            <span class="price-audit-apply-progress__icon"><b-spinner variant="primary" /></span>
            <strong>Guardando ajustes</strong>
            <span>Estamos recalculando la utilidad de {{ bulkModalValidCount }} producto{{ bulkModalValidCount == 1 ? '' : 's' }}.</span>
          </div>
        </template>

        <section :aria-busy="applying ? 'true' : 'false'">
          <div class="price-audit-apply-info">
            <span class="price-audit-apply-info__icon"><AppIcon name="price_check" /></span>
            <div>
              <strong>El precio final define el ajuste</strong>
              <p>Enviamos el importe objetivo de cada producto. El sistema recalcula la utilidad exacta con el costo y el IVA vigentes.</p>
              <span class="price-audit-apply-info__note"><AppIcon name="verified" /> La utilidad de la tabla es una estimación.</span>
            </div>
          </div>

          <div v-if="bulkModalInvalidCount" class="price-audit-bulk-notice price-audit-bulk-notice--warning">
            <AppIcon name="exclamation-triangle" />
            <span>{{ bulkModalInvalidCount }} producto{{ bulkModalInvalidCount == 1 ? '' : 's' }} no se puede{{ bulkModalInvalidCount == 1 ? '' : 'n' }} ajustar automáticamente y se va{{ bulkModalInvalidCount == 1 ? '' : 'n' }} a omitir. Requiere{{ bulkModalInvalidCount == 1 ? '' : 'n' }} una revisión manual.</span>
          </div>
          <div v-if="bulkModal.preview.length > bulkModalPreviewLimit" class="price-audit-bulk-notice">
            Mostrando los primeros {{ bulkModalPreviewLimit }} de {{ bulkModal.preview.length }} productos seleccionados — la regla se aplica a los {{ bulkModal.preview.length }} igual.
          </div>

          <b-table
            class="app-table"
            small
            :items="bulkModalPreviewVisible"
            :fields="bulkModalFields"
            sticky-header="45vh"
            responsive
            fixed
          >
            <template v-slot:cell(product_name)="data">{{ data.item.row.product_name }}</template>
            <template v-slot:cell(oldPrice)="data">{{ fPrice(data.item.oldPrice) }}</template>
            <template v-slot:cell(newPrice)="data"><b>{{ fPrice(data.item.newPrice) }}</b></template>
            <template v-slot:cell(newUtility)="data">
              <span v-if="data.item.invalid" class="text-warning">{{ data.item.invalidReason }}</span>
              <span v-else>≈ {{ fUtility(data.item.newUtility) }}%</span>
            </template>
          </b-table>
        </section>
      </b-overlay>
    </b-modal>

  </b-row>
</template>

<script>
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Error from '@/handler/error'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import pricingServiceAPI from '@/components/inc/productPricing/services'
  import productsServiceAPI from './services'
  import { effectiveUtility, roundToNearestMultiple, roundUtility } from '@/components/inc/productPricing/pricingMath'

  export default {
    components: {
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.PRODUCTOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'priceAudit',
          elements: {},
        },
        isMobileViewport: false,
        filtersOpen: false,
        loading: false,
        applying: false,
        priceListOptions: [],
        reviewRuleOptions: [
          { code: 'has_cents', label: 'Con centavos' },
          { code: 'not_multiple_5', label: 'Pendientes de redondear a 5' },
          { code: 'not_multiple_10', label: 'Pendientes de redondear a 10' },
          { code: 'not_multiple_100', label: 'Pendientes de redondear a 100' },
        ],
        filters: {
          priceList: null,
          search: '',
          priceMin: null,
          priceMax: null,
          // La auditoria siempre inicia sobre precios a revisar: no se muestra
          // el catalogo completo sin una regla que lo justifique.
          dirtyRule: { code: 'has_cents', label: 'Con centavos' },
        },
        rows: [],
        pagination: { current_page: 1, last_page: 1, total: 0, per_page: 100 },
        selected: [],
        selectingAll: false,
        snapshot: { loading: false, active: false, startedAt: null, confirm: false },
        snapshotPollTimer: null,
        bulkModal: { show: false, label: '', multiple: 1, preview: [] },
        bulkConfirm: { show: false },
      }
    },
    created() {
      Helper.hasAccessView(this.access)
      this.filtersOpen = StorageLocal.getValue('filters_open_priceAudit') == 'true'
      this.loadPriceLists()
      this.checkSnapshotStatus().then(active => {
        if(!active) this.search()
      })
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
    },
    beforeDestroy() {
      this.stopSnapshotPolling()
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_priceAudit', value ? 'true' : 'false')
      },
    },
    computed: {
      fields() {
        return [
          { key: 'select_multi', label: '', class: 'align-middle ' + this.alignClass('text-center'), width: '30px' },
          { key: 'product_name', label: 'Producto', class: 'align-middle' },
          { key: 'price_list_name', label: 'Lista', class: 'align-middle' },
          { key: 'price', label: 'Precio final', class: 'align-middle ' + this.alignClass('text-right') },
          { key: 'utility', label: 'Utilidad', class: 'align-middle ' + this.alignClass('text-right') },
          { key: 'status', label: '', class: 'align-middle text-center price-audit-status-cell' },
        ]
      },
      bulkModalFields() {
        return [
          { key: 'product_name', label: 'Producto', thStyle: { width: '52%' } },
          { key: 'oldPrice', label: 'Precio actual', thStyle: { width: '15%' } },
          { key: 'newPrice', label: 'Precio nuevo', thStyle: { width: '15%' } },
          { key: 'newUtility', label: 'Utilidad estimada', thStyle: { width: '18%' } },
        ]
      },
      oldestSnapshotAt() {
        const dates = this.rows.map(row => row.snapshot_updated_at).filter(Boolean)
        if(!dates.length) return null
        return dates.reduce((a, b) => (a < b ? a : b))
      },
      oldestSnapshotAgeLabel() {
        return this.oldestSnapshotAt ? this.moment(this.oldestSnapshotAt).fromNow() : ''
      },
      reviewRuleHint() {
        const rule = this.filters.dirtyRule ? this.filters.dirtyRule.code : null
        const hints = {
          has_cents: 'Muestra precios con decimales. Después elegís a qué base redondearlos.',
          not_multiple_5: 'Muestra sólo precios que cambiarían al redondear a múltiplos de 5.',
          not_multiple_10: 'Muestra sólo precios que cambiarían al redondear a múltiplos de 10.',
          not_multiple_100: 'Muestra sólo precios que cambiarían al redondear a múltiplos de 100.',
        }
        return hints[rule] || 'Elegí una regla para enfocar la revisión, o dejala vacía para ver todos los precios.'
      },
      bulkModalValidCount() {
        return this.bulkModal.preview.filter(preview => !preview.invalid).length
      },
      bulkModalInvalidCount() {
        return this.bulkModal.preview.filter(preview => preview.invalid).length
      },
      // Con "seleccionar todos" puede haber miles de filas en bulkModal.preview
      // -- renderizar esa tabla completa en el modal la vuelve lenta. La regla
      // igual se aplica a todas (confirmBulk usa bulkModal.preview completo,
      // no este recorte), esto es solo para que el modal no se cuelgue.
      bulkModalPreviewLimit() {
        return 50
      },
      bulkModalPreviewVisible() {
        return this.bulkModal.preview.slice(0, this.bulkModalPreviewLimit)
      },
    },
    methods: {
      updateMobileViewport(event) {
        this.isMobileViewport = event.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      fPrice(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(this.toNumber(value))
      },
      fUtility(value) {
        return Math.round(this.toNumber(value) * 100) / 100
      },
      snapshotAge(value) {
        return this.moment(value).fromNow()
      },
      snapshotExactTime(value) {
        return this.moment(value).format('DD/MM/YYYY HH:mm:ss')
      },
      isSnapshotNewerThanBase(row) {
        return !!(row.snapshot_updated_at && this.oldestSnapshotAt &&
          this.moment(row.snapshot_updated_at).isAfter(this.oldestSnapshotAt))
      },
      snapshotBadgeTitle(row) {
        const exactTime = this.snapshotExactTime(row.snapshot_updated_at)
        return this.isSnapshotNewerThanBase(row)
          ? exactTime + ' — actualizado después de la foto base'
          : exactTime
      },
      toNumber(value) {
        const number = parseFloat(value)
        return isNaN(number) ? 0 : number
      },
      isNum(value) {
        return value !== null && value !== undefined && value !== '' && !isNaN(value)
      },
      effectiveUtilityOf(row) {
        return effectiveUtility({ utility: row.utility, defaultUtility: row.price_list_default_utility })
      },
      isUsingListDefault(row) {
        return this.toNumber(row.utility) <= 0 && this.toNumber(row.price_list_default_utility) > 0
      },
      loadPriceLists() {
        productsServiceAPI.obtenerListaPreciosSimple()
          .then(response => {
            this.priceListOptions = (response.data || []).map(item => ({ code: item.id, label: item.name }))
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
      },
      checkSnapshotStatus() {
        return pricingServiceAPI.obtenerEstadoSnapshotPrecios()
          .then(response => {
            const data = response.data || {}
            const wasActive = this.snapshot.active
            this.snapshot.active = !!data.active
            this.snapshot.startedAt = data.started_at || null

            if(this.snapshot.active) {
              // Si el proceso fue iniciado desde otra pestaña/usuario, cerrar
              // cualquier interacción pendiente: no se aplican ajustes sobre
              // un snapshot que todavía se está recalculando.
              this.selected = []
              this.bulkModal.show = false
              this.bulkConfirm.show = false
              this.startSnapshotPolling()
              return true
            }

            this.stopSnapshotPolling()
            if(wasActive) {
              this.$awn.success('Precios actualizados. Ya podés iniciar la auditoría.')
              return this.search().then(() => false)
            }
            return false
          })
          .catch(() => false)
      },
      startSnapshotPolling() {
        if(this.snapshotPollTimer) return
        this.snapshotPollTimer = setInterval(() => this.checkSnapshotStatus(), 2500)
      },
      stopSnapshotPolling() {
        if(!this.snapshotPollTimer) return
        clearInterval(this.snapshotPollTimer)
        this.snapshotPollTimer = null
      },
      // Payload compartido por la busqueda paginada y por "seleccionar todos
      // los resultados" (selectAllMatching) -- los mismos filtros, para que
      // "todos los resultados" sea siempre exactamente lo que se ve filtrado.
      buildFilterPayload() {
        return {
          price_list_id: this.filters.priceList ? this.filters.priceList.code : null,
          search: this.filters.search || null,
          price_min: this.isNum(this.filters.priceMin) ? this.filters.priceMin : null,
          price_max: this.isNum(this.filters.priceMax) ? this.filters.priceMax : null,
          review_rule: this.filters.dirtyRule ? this.filters.dirtyRule.code : null,
        }
      },
      search() {
        if(this.snapshot.active) return Promise.resolve()
        this.loading = true
        const payload = {
          ...this.buildFilterPayload(),
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
        }
        return pricingServiceAPI.auditarPrecios(payload)
          .then(response => {
            const data = response.data || {}
            this.rows = data.data || []
            this.pagination = {
              current_page: data.current_page || 1,
              last_page: data.last_page || 1,
              total: data.total || 0,
              per_page: data.per_page || this.pagination.per_page,
            }
            this.selected = []
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
          .finally(() => { this.loading = false })
      },
      // Después de aplicar un ajuste de auditoría, el snapshot en la base ya
      // queda al día de una (ver PriceListProductsController::
      // refreshRowSnapshot), asi que alcanza con volver a buscar -- ya no
      // hace falta traer el precio en vivo fila por fila. matchKey/matchIds
      // solo se usan para marcar "Actualizado" en las filas tocadas que
      // sigan visibles despues de la busqueda.
      highlightAfterEdit(matchIds, matchKey) {
        const wanted = (matchIds || []).filter(Boolean)
        return this.search().then(() => {
          if(!wanted.length) return
          this.rows.forEach(row => {
            // $set: just_updated no viene en la respuesta del backend, y
            // Vue 2 no reacciona a agregar una propiedad nueva por
            // asignacion directa sobre un objeto ya reactivo.
            if(wanted.includes(row[matchKey])) this.$set(row, 'just_updated', true)
          })
        })
      },
      openSnapshotConfirmation() {
        if(this.snapshot.loading || this.snapshot.active) return
        this.snapshot.confirm = true
      },
      refreshSnapshot() {
        if(this.snapshot.loading || this.snapshot.active) return
        this.snapshot.loading = true
        pricingServiceAPI.forzarSnapshotPrecios()
          .then(() => {
            this.snapshot.confirm = false
            this.snapshot.active = true
            this.selected = []
            this.startSnapshotPolling()
          })
          .catch(error => {
            this.checkSnapshotStatus()
            this.$awn.alert(Error.showError(error))
          })
          .finally(() => { this.snapshot.loading = false })
      },
      isSelected(item) {
        return this.selected.some(row => row.id === item.id)
      },
      toggleSelect(item) {
        if(this.snapshot.active) return
        const index = this.selected.findIndex(row => row.id === item.id)
        if(index === -1) {
          this.selected.push(item)
        } else {
          this.selected.splice(index, 1)
        }
      },
      selectPage() {
        if(this.snapshot.active) return
        const newOnes = this.rows.filter(row => !this.isSelected(row))
        this.selected = this.selected.concat(newOnes)
      },
      // Trae los resultados que matchean el filtro actual (no solo la pagina
      // visible) para aplicarles una regla en lote. Si supera el tope seguro,
      // el backend devuelve los primeros 3.000 y avisa que la seleccion quedo
      // acotada, sin perder la posibilidad de trabajar con ese lote.
      selectAllMatching() {
        if(this.snapshot.active) return
        this.selectingAll = true
        const payload = { ...this.buildFilterPayload(), all_matching: true }
        pricingServiceAPI.auditarPrecios(payload)
          .then(response => {
            const data = response.data || {}
            this.selected = data.data || []
            if(data.limited) {
              this.$awn.success(`Se seleccionaron los primeros ${data.selected_total} de ${data.total} resultados.`)
            }
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
          .finally(() => { this.selectingAll = false })
      },
      clearSelection() {
        this.selected = []
      },
      // Estimacion SOLO para la previsualizacion (no se manda al backend):
      // deriva una utilidad aproximada sin pedir costo/descuento/IVA reales,
      // reconstruyendo K = costo x (1-desc/100) x (1+iva/100) a partir del
      // precio actual + utilidad actual. Como parte de esa base (precio
      // actual) ya viene redondeada a 2 decimales, esta estimacion puede
      // errar por un centavo -- por eso el ajuste real que se guarda (ver
      // confirmBulk) manda el PRECIO objetivo, no esta utilidad, y el
      // backend calcula la utilidad exacta con los valores reales
      // (PriceListProductsController::priceAuditApply).
      computeNewUtility(row, newPrice) {
        const oldUtility = this.effectiveUtilityOf(row)
        const oldPrice = this.toNumber(row.price)
        const ivaFactorEquivalent = 1 + oldUtility / 100
        if(!(oldPrice > 0) || !(ivaFactorEquivalent > 0)) return null
        const k = oldPrice / ivaFactorEquivalent
        if(!(k > 0)) return null
        return roundUtility((this.toNumber(newPrice) / k - 1) * 100)
      },
      openBulkModal(multiple, label) {
        if(this.snapshot.active) return
        this.bulkModal.multiple = multiple
        this.bulkModal.label = label
        this.bulkModal.preview = this.selected.map(row => {
          const newPrice = roundToNearestMultiple(row.price, multiple)
          const newUtility = this.computeNewUtility(row, newPrice)
          return {
            row,
            oldPrice: row.price,
            newPrice,
            newUtility,
            invalid: newUtility === null || newUtility < 0,
            invalidReason: newUtility === null
              ? 'Sin base calculable'
              : 'Requiere utilidad negativa',
          }
        })
        this.bulkModal.show = true
      },
      openBulkConfirmation() {
        if(this.snapshot.active || this.applying || !this.bulkModalValidCount) return
        this.bulkConfirm.show = true
      },
      confirmBulk() {
        if(this.snapshot.active) return
        // Se manda el precio objetivo, no la utilidad estimada de la
        // previsualizacion: el backend calcula la utilidad exacta con el
        // costo y el IVA reales (ver comentario de computeNewUtility).
        const items = this.bulkModal.preview
          .filter(preview => !preview.invalid)
          .map(preview => ({ id: preview.row.id, target_price: preview.newPrice }))

        if(!items.length) {
          this.$awn.alert('No hay productos válidos para ajustar')
          return
        }

        // Con "seleccionar todos" puede haber hasta 3.000 filas. El backend
        // ya precarga costos y relaciones por tanda; usar 1.000 reduce los
        // viajes HTTP de 15 a 3 sin armar requests excesivamente grandes.
        const chunkSize = 1000
        const chunks = []
        for(let i = 0; i < items.length; i += chunkSize) {
          chunks.push(items.slice(i, i + chunkSize))
        }

        this.applying = true
        this.bulkConfirm.show = false
        chunks.reduce((promise, chunk) => promise.then(() => pricingServiceAPI.aplicarAjustePrecios({ items: chunk })), Promise.resolve())
          .then(() => {
            this.$awn.success(`Se actualizaron ${items.length} precio(s)`)
            this.bulkModal.show = false
            const ids = items.map(item => item.id)
            this.selected = []
            this.highlightAfterEdit(ids, 'id')
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
          .finally(() => { this.applying = false })
      },
    },
  }
</script>

<style scoped>
  .price-audit-snapshot-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
    padding: 9px 11px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
  }
  .price-audit-snapshot-status--active {
    border-color: var(--app-color-primary-border);
    background: var(--app-color-primary-soft);
  }
  .price-audit-snapshot-status__identity {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
  }
  .price-audit-snapshot-status__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: var(--app-color-primary);
    background: var(--app-color-primary-soft);
  }
  .price-audit-snapshot-status--active .price-audit-snapshot-status__icon {
    color: var(--app-color-on-primary);
    background: var(--app-color-primary);
  }
  .price-audit-snapshot-status__detail {
    margin-top: 1px;
    color: var(--app-color-text-secondary);
    font-size: 12px;
  }
  .price-audit-snapshot-status__action {
    flex-shrink: 0;
  }
  .price-audit-workspace {
    display: block;
  }
  .price-audit-process-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 330px;
    gap: 8px;
    text-align: center;
    color: var(--app-color-text-secondary);
  }
  .price-audit-process-overlay strong {
    color: var(--app-color-text);
  }
  .price-audit-process-overlay span {
    font-size: 13px;
  }
  .price-audit-product-name {
    color: var(--app-color-text);
    font-weight: 600;
  }
  .price-audit-product-code {
    color: var(--app-color-text-secondary);
    font-size: 11px;
    letter-spacing: .01em;
  }
  .price-audit-list-badge {
    border: 1px solid var(--app-color-border);
    color: var(--app-color-text-secondary);
    font-weight: 600;
  }
  .price-audit-price-cell,
  .price-audit-utility-cell {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }
  .price-audit-price-cell__value {
    color: var(--app-color-primary);
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    letter-spacing: .01em;
  }
  .price-audit-updated-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 6px;
    font-size: 10px;
    font-weight: 600;
  }
  .price-audit-utility-badge {
    min-width: 52px;
    padding: 4px 7px;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }
  .price-audit-utility-cell__source {
    color: var(--app-color-text-secondary);
    font-size: 10px;
  }
  .price-audit-apply-info {
    display: flex;
    gap: 11px;
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid var(--app-color-primary-border);
    border-radius: 8px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-text-secondary);
  }
  .price-audit-apply-info__icon,
  .price-audit-apply-progress__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: var(--app-color-on-primary);
    background: var(--app-color-primary);
  }
  .price-audit-apply-info strong {
    display: block;
    margin-bottom: 2px;
    color: var(--app-color-text);
  }
  .price-audit-apply-info p {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
  }
  .price-audit-apply-info__note {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 7px;
    color: var(--app-color-primary);
    font-size: 11px;
    font-weight: 600;
  }
  .price-audit-bulk-notice {
    margin-bottom: 10px;
    padding: 8px 10px;
    border: 1px solid var(--app-color-border);
    border-radius: 6px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text-secondary);
    font-size: 12px;
  }
  .price-audit-bulk-notice--warning {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    border-color: #f0c36a;
    background: #fff8e8;
    color: #735215;
  }
  .price-audit-apply-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 300px;
    gap: 8px;
    text-align: center;
    color: var(--app-color-text-secondary);
  }
  .price-audit-apply-progress__icon {
    width: 42px;
    height: 42px;
    background: var(--app-color-primary-soft);
  }
  .price-audit-apply-progress strong {
    color: var(--app-color-text);
  }
  .price-audit-apply-progress span:last-child {
    font-size: 13px;
  }
  .price-audit-select-bar {
    justify-content: space-between;
    gap: 12px;
  }
  .price-audit-row-select {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 0;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: var(--app-color-primary);
    cursor: pointer;
    line-height: 1;
  }
  .price-audit-row-select__label {
    display: none;
  }
  .price-audit-row-select--selected {
    color: var(--app-color-text);
  }
  .price-audit-row-select:focus-visible {
    outline: 2px solid var(--app-color-warning);
    outline-offset: 2px;
  }
  .price-audit-bulk-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .price-audit-bulk-actions__count {
    font-size: 12px;
    color: var(--app-color-text-secondary);
    margin-right: 4px;
  }
  .price-audit-review-hint {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin-top: 6px;
    color: var(--app-color-text-secondary);
    font-size: 11px;
    line-height: 1.25;
  }
  @media (max-width: 575.98px) {
    .price-audit-snapshot-status {
      align-items: flex-start;
    }
    .price-audit-snapshot-status__action {
      width: 100%;
      justify-content: center;
    }
    .price-audit-price-cell,
    .price-audit-utility-cell {
      align-items: flex-start;
    }
    .price-audit-apply-info {
      align-items: flex-start;
    }
  }
  @media (max-width: 767.98px) {
    .price-audit-status-cell {
      text-align: left !important;
    }
    .price-audit-status-content {
      width: 100%;
      text-align: left !important;
    }
    .price-audit-row-select {
      min-height: 30px;
      padding: 6px 9px;
      border: 1px solid var(--app-color-warning-border);
      background: var(--app-color-warning-soft);
      color: var(--app-color-warning-hover);
      font-size: 12px;
      font-weight: 600;
    }
    .price-audit-row-select--selected {
      border-color: var(--app-color-warning);
      background: var(--app-color-warning);
      color: #fff;
    }
    .price-audit-row-select__label {
      display: inline;
    }
  }
</style>
